<template lang="html">
  <report-builder
    v-model="reportFilters"
    title="Blockers Report"
    type="blockers"
    command="diningBlockerReport"
    saved-report-command="diningSavedReport"
    :resources="reportFilterResourceCommands"
    :defaults="defaultReportFilters"
    :headers="headers"
    :report-fields="includeFields"
    :report-filters="currentFilterSelection"
    :report-order="sortOrder"
  >
    <!-- REPORT FORM -->

    <!--  DATE SEARCH TYPE SELECT  -->
    <template v-slot:filter-type>
      <v-autocomplete
        v-model="reportFilters.dateSearchType"
        prepend-icon="mdi-cog-box"
        :items="dateSearchOptions"
        item-text="text"
        item-value="value"
        label="Date Search Type"
        clearable
        @click:clear="$nextTick(() => reportFilters.dateSearchType = null)"
      />
    </template>

    <!--  RANGE  -->
    <template v-slot:filter-date-range>
      <v-layout v-if="filterByRange">
        <v-flex class="xs6">
          <date-picker
            v-model="reportFilters.startDate"
            label="Start Date"
          />
        </v-flex>
        <v-flex class="xs6 pl-1">
          <date-picker
            v-model="reportFilters.endDate"
            :icon="null"
            label="End Date"
            clearable
          />
        </v-flex>
      </v-layout>
    </template>

    <!--  MONTH / YEAR  -->
    <template v-slot:filter-date_ym>
      <v-layout v-if="filterByYearMonth">
        <v-flex class="xs6">
          <v-autocomplete
            v-model="reportFilters.month"
            prepend-icon="mdi-calendar"
            label="Month"
            :items="monthsSelect"
            item-text="text"
            item-value="value"
            clearable
            @click:clear="$nextTick(() => reportFilters.month = null)"
          />
        </v-flex>
        <v-flex class="xs6 pl-1">
          <v-autocomplete
            v-model="reportFilters.year"
            label="Year"
            :items="yearSelect"
            item-text="text"
            item-value="value"
            required
          />
        </v-flex>
      </v-layout>
    </template>

    <!--  REPORT LOCATION  -->
    <template v-slot:filter-location>
      <v-autocomplete
        v-model="reportFilters.locationId"
        prepend-icon="mdi-map-marker"
        label="Location"
        :items="locations"
        item-text="name"
        item-value="id"
        auto
      />
    </template>

    <!--  REPORT SORT ORDER  -->
    <template v-slot:filter-sortby>
      <v-autocomplete
        v-model="reportFilters.sortby"
        prepend-icon="mdi-sort"
        label="Sort Order"
        :items="sortOptions"
        item-text="text"
        item-value="value"
      />
    </template>

    <!--  SHOW / HIDE DISABLED BLOCKERS  -->
    <template v-slot:filter-disabled>
      <v-boolean
        v-model="reportFilters.hideDisabled"
        label="Hide Disabled"
        hide-details
      />
    </template>

    <!-- REPORT HEADERS -->
    <!-- (No header slot overrides needed) -->

    <!-- REPORT ROWS/COLUMNS -->
    <template
      slot="td-dates"
      slot-scope="props"
    >
      <div>{{ prettyDates(props.row) }}</div>
    </template>
    <template
      slot="td-times"
      slot-scope="props"
    >
      <div>{{ prettyTimes(props.row) }}</div>
    </template>
    <template
      slot="td-days"
      slot-scope="props"
    >
      <div>{{ showDays(props.row) }}</div>
    </template>
    <!-- <template slot="td-disabled" slot-scope="props">{{ (props.item ? 'X' : '') }}</template> -->
  </report-builder>
</template>

<script>
import {values, helpers, rules, ReportBuilder} from '@/ft-vue-core'
import moment from 'moment'

const {deepClone, pad} = helpers
const {monthsProper, daysLower, daysShort} = values

const defaultReportFilters = {
  scale: 1,
  locationId: null,
  sortby: 'startDate',
  hideDisabled: true,
  dateSearchType: null,
  month: null,
  year: null,
  startDate: null,
  endDate: null,
  paperSize: null, // Defaults to LETTER
  orientation: null, // Defaults to PORTRAIT
  email: null
}

export default {
  name: 'BlockersReport',
  components: {
    ReportBuilder
  },
  mixins: [rules],
  data: function () {
    return {
      defaultReportFilters: deepClone(defaultReportFilters),
      reportFilters: deepClone(defaultReportFilters),
      sortOptions: [
        {text: 'Name', value: 'name'},
        {text: 'Date/Time', value: 'startDate'},
        {text: 'Location - Date/Time', value: 'locationDateTime'},
        // {text: 'Location', value: 'locationId'},
        {text: 'Reason', value: 'reason'}
      ],
      dateSearchOptions: [
        {text: 'All Future', value: 'future'},
        {text: 'By Month/Year', value: 'month'},
        {text: 'Date Range', value: 'range'}
      ],
      hideDisabledOptions: [
        {text: 'Include Disabled', value: false},
        {text: 'Exclude Disabled', value: true}
      ],
      locations: []
    }
  },
  computed: {
    filterByRange () {
      return this.reportFilters.dateSearchType === 'range'
    },
    filterByYearMonth () {
      return this.reportFilters.dateSearchType === 'month'
    },
    headers () {
      return [
        {text: 'Name', field: 'name'},
        ...this.reportFilters.sortby === 'locationDateTime' ? [{text: 'Location', field: 'locationName'}] : [{text: 'Locations', field: 'locationNames'}],
        {text: 'Dates', field: 'dates', slot: true},
        {text: 'Times', field: 'times', slot: true},
        {text: 'Days', field: 'days', slot: true},
        {text: 'Reason', field: 'reason'},
        {text: 'Updated', field: 'updated'},
        ...this.reportFilters.hideDisabled ? [] : [{text: 'Disabled', field: 'disabled', type: 'FLAG'}]
      ]
    },
    yearSelect () {
      // TODO:  Build dynamic range
      return [
        {text: '2017', value: 2017},
        {text: '2018', value: 2018},
        {text: '2019', value: 2019}
      ]
    },
    monthsSelect () {
      return monthsProper.reduce((result, month, i) => {
        result.push({text: month, value: pad(i + 1, 2)})
        return result
      }, [])
    }
  },
  methods: {
    showDays (row) {
      const result = []
      var foundAll = true
      for (let i = 0, l = daysLower.length; i < l; i++) {
        if (row[daysLower[i]]) {
          result.push(daysShort[i])
        } else {
          foundAll = false
        }
      }
      if (foundAll && result.length) return 'All'
      return result.join(', ')
    },
    prettyTimes (row) {
      if (!row) return
      var stime = moment(row.startDate + ' ' + row.startTime)
      var etime = moment(row.endDate + ' ' + row.endTime)
      return stime.format('h:mm A') + ' - ' + etime.format('h:mm A')
    },
    prettyDates (row) {
      if (!row) return
      var stime = moment(row.startDate + ' ' + row.startTime)
      var etime = moment(row.endDate + ' ' + row.endTime)
      return stime.format('L') + ' - ' + etime.format('L')
    },
    sortOrder (f) {
      switch (f.sortby) {
      case 'locationDateTime':
        return ['locationName:ASC', 'startDate:ASC', 'startTime:ASC']
      case 'startDate':
        return ['startDate:ASC', 'startTime:ASC']
      default:
        return [f.sortby + ':ASC']
      }
    },
    includeFields (f) {
      const result = ['locationNames', 'updated']
      result.push(...daysLower)
      if (f.sortby === 'locationDateTime') {
        result.push('locationName')
      }
      return result
    },
    currentFilterSelection (f) {
      var filters = []
      var beginDate, endDate
      if (f.locationId) {
        filters.push({
          field: 'id',
          operator: 'IN',
          subQueryTarget: 'diningBlockerToLocation.blockerId',
          subQueryFilter: [{field: 'locationId', operator: 'EQUAL', values: [f.locationId]}]
        })
      }
      if (f.hideDisabled) filters.push({field: 'disabled', operator: 'EQUAL', values: [false]})
      if (f.dateSearchType === 'future') {
        filters.push({field: 'endDate', operator: 'GREATER_OR_EQUAL', values: [moment().format('YYYY-MM-DD')]})
      } else if (f.dateSearchType === 'month') {
        if (f.month == null) {
          // entire year, set begin to 1/1 or selected year and end to 12/31 of same year
          beginDate = f.year + '-01-01'
          endDate = f.year + '-12-31'
        } else {
          // specific month of the year
          var daysInMo = moment(f.year + '-' + f.month).daysInMonth()
          beginDate = f.year + '-' + f.month + '-00'
          endDate = f.year + '-' + f.month + '-' + daysInMo
        }
        filters.push(
          {field: 'endDate', operator: 'GREATER_OR_EQUAL', values: [beginDate]},
          {field: 'startDate', operator: 'LESS_OR_EQUAL', values: [endDate]}
        )
      } else if (f.dateSearchType === 'range') {
        beginDate = f.startDate
        endDate = f.endDate
        filters.push(
          {filter: [
            {field: 'startDate', operator: 'BETWEEN', values: [beginDate, endDate]},
            {condition: 'OR', field: 'endDate', operator: 'BETWEEN', values: [beginDate, endDate]}
          ]}
        )
      }
      console.log('Selected Filters', filters)
      return filters
    },
    validFilters () {
      return true
    },
    reportFilterResourceCommands () {
      return [
        {
          command: 'diningLocation',
          load: this.locations,
          resultFilter (data) {
            data.unshift({name: 'All Locations', id: null})
            return data
          }
        }
      ]
    }
  }
}
</script>
